import { useState } from 'react'
import { Info, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { Preset } from '@/hooks/useLinkBuilderDataEntry'

// Radix Select requires non-empty string values; this sentinel represents
// "no preset selected" without conflicting with any real preset id.
const NONE_VALUE = '__none__'

interface PresetsSectionProps {
  presets: Preset[]
  activePresetId: string | null
  isModified: boolean
  isBusy: boolean
  hasAnyParams: boolean
  onApply: (presetId: string) => void
  onSaveNew: (name: string) => Promise<void>
  onUpdate: () => Promise<void>
  onDelete: () => Promise<void>
}

export function PresetsSection({
  presets,
  activePresetId,
  isModified,
  isBusy,
  hasAnyParams,
  onApply,
  onSaveNew,
  onUpdate,
  onDelete,
}: PresetsSectionProps) {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  const sorted = [...presets].sort((a, b) => a.name.localeCompare(b.name))
  const activePreset = activePresetId === null ? null : sorted.find((p) => p.id === activePresetId)
  const hasPresets = sorted.length > 0
  const showSaveNew = hasAnyParams

  if (!hasPresets && !showSaveNew) {
    return null
  }

  return (
    <section className="space-y-2">
      {hasPresets ? (
        <div className="flex gap-2">
          <Select
            value={activePresetId ?? NONE_VALUE}
            onValueChange={(v) => v !== NONE_VALUE && onApply(v)}
            disabled={isBusy}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Pick a preset…" />
            </SelectTrigger>
            <SelectContent>
              {sorted.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {activePreset && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Delete preset ${activePreset.name}`}
              disabled={isBusy}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              <Trash2 className="size-4" aria-hidden />
            </Button>
          )}

          <PresetInfoTooltip />
        </div>
      ) : null}

      {(showSaveNew || (activePreset && isModified)) && (
        <div className="flex gap-2">
          {activePreset && isModified && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isBusy}
              onClick={() => setConfirmUpdateOpen(true)}
              className="flex-1"
            >
              Update preset
            </Button>
          )}
          {showSaveNew && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isBusy}
              onClick={() => setSaveDialogOpen(true)}
              className="flex-1"
            >
              Save as new preset…
            </Button>
          )}
          {!hasPresets && <PresetInfoTooltip />}
        </div>
      )}

      <SavePresetDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onConfirm={async (name) => {
          await onSaveNew(name)
          setSaveDialogOpen(false)
        }}
      />

      <ConfirmUpdateDialog
        open={confirmUpdateOpen}
        onOpenChange={setConfirmUpdateOpen}
        onConfirm={async () => {
          await onUpdate()
          setConfirmUpdateOpen(false)
        }}
      />

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        presetName={activePreset?.name ?? ''}
        onConfirm={async () => {
          await onDelete()
          setConfirmDeleteOpen(false)
        }}
      />
    </section>
  )
}

interface SavePresetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (name: string) => Promise<void>
}

function SavePresetDialog({ open, onOpenChange, onConfirm }: SavePresetDialogProps) {
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const trimmed = name.trim()
  const canSave = trimmed !== '' && !submitting

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) {
          setName('')
          setSubmitting(false)
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save as new preset</DialogTitle>
          <DialogDescription>
            Save the current set of parameters so others can re-use them easily.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="preset-name">Name</Label>
          <Input
            id="preset-name"
            autoFocus
            spellCheck={false}
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Facebook"
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={!canSave}
            onClick={async () => {
              setSubmitting(true)
              try {
                await onConfirm(trimmed)
                setName('')
              } finally {
                setSubmitting(false)
              }
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PresetInfoTooltip() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="What is a preset?"
          className="self-center text-muted-foreground hover:text-foreground"
        >
          <Info className="size-3.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-[20rem]">
        Save the parameters that stay the same for a use case (e.g. utm_source=facebook) and
        leave the ones that vary (e.g. utm_campaign) blank — so others can build links by
        filling in just what changes.
      </TooltipContent>
    </Tooltip>
  )
}

interface ConfirmUpdateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void>
}

function ConfirmUpdateDialog({ open, onOpenChange, onConfirm }: ConfirmUpdateDialogProps) {
  const [submitting, setSubmitting] = useState(false)

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) setSubmitting(false)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update preset?</DialogTitle>
          <DialogDescription>This will update the preset for all users.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              No
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true)
              try {
                await onConfirm()
              } finally {
                setSubmitting(false)
              }
            }}
          >
            Yes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface ConfirmDeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  presetName: string
  onConfirm: () => Promise<void>
}

function ConfirmDeleteDialog({
  open,
  onOpenChange,
  presetName,
  onConfirm,
}: ConfirmDeleteDialogProps) {
  const [submitting, setSubmitting] = useState(false)

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) setSubmitting(false)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete preset?</DialogTitle>
          <DialogDescription>
            “{presetName}” will be removed for all users. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true)
              try {
                await onConfirm()
              } finally {
                setSubmitting(false)
              }
            }}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
